import { Badge, Flex, Spinner, Table, Text } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { IBaseDialog } from 'interfaces/i-dialogs';
import {
  LOCAL_DATETIME_FORMAT_LONG,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { FC, useMemo } from 'react';
import useMachineStatusStore from 'stores/use-machine-status-store';

const TRUTHY_VALUES = [true, 'True'];

type MachineStateKey =
  | keyof IMachineState
  | 'firing'
  | 'ball_count'
  | 'video_uuid'
  | 'od_busy';

const PROC_CATEGORIES: {
  name: string;
  required: MachineStateKey[];
  optional?: MachineStateKey[];
}[] = [
  {
    name: 'common.position',
    required: ['px', 'py', 'pz'],
  },
  {
    name: 'common.wheels',
    required: ['w1', 'w2', 'w3'],
  },
  {
    name: 'common.angles',
    required: ['a1', 'a2', 'a3', 'tilt', 'yaw'],
  },
  {
    name: 'common.ball',
    required: ['firing', 'ball_count'],
  },
  {
    name: 'common.orientation',
    required: ['qw', 'qx', 'qy', 'qz'],
  },
  {
    name: 'common.miscellaneous',
    required: ['video_uuid', 'training'],
    optional: ['od_busy'],
  },
];

interface IR2fCategory {
  name: string;
  ready: string[];
  notReady: string[];
}

const TableContent = () => {
  const r2fData = useMachineStatusStore((s) => s.r2fData);

  const rows = useMemo(() => {
    if (!r2fData) {
      return null;
    }

    const r2fDict = r2fData.data as unknown as DictOf<boolean>;

    const output: IR2fCategory[] = PROC_CATEGORIES.map((c) => {
      const r: IR2fCategory = {
        name: c.name,
        ready: [],
        notReady: [],
      };

      c.required.forEach((key) => {
        const ready = r2fDict[key] === true;

        if (TRUTHY_VALUES.includes(ready)) {
          r.ready.push(key);
        } else {
          r.notReady.push(key);
        }
      });

      if (c.optional) {
        c.optional.forEach((key) => {
          const ready = r2fDict[key];

          switch (ready) {
            case true: {
              r.ready.push(key);
              break;
            }

            case false: {
              r.notReady.push(key);
              break;
            }

            default: {
              // don't do anything, the optional key wasn't sent
              break;
            }
          }
        });
      }

      return r;
    });

    return output;
  }, [r2fData]);

  if (!rows) {
    return <Spinner />;
  }

  return (
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>
            {t('common.category')}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>{t('common.ready')}</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>
            {t('common.not-ready')}
          </Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row, i) => (
          <Table.Row key={i}>
            <Table.Cell>{t(row.name)}</Table.Cell>
            <Table.Cell>
              {row.ready.map((m, j) => (
                <Badge key={`r-${j}`} mr="1" color={RADIX.COLOR.SUCCESS}>
                  {m}
                </Badge>
              ))}
            </Table.Cell>
            <Table.Cell>
              {row.notReady.map((m, j) => (
                <Badge key={`nr-${j}`} mr="1" color={RADIX.COLOR.DANGER}>
                  {m}
                </Badge>
              ))}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
};

export const R2fStatusDialog: FC<IBaseDialog> = (props) => {
  const r2fDate = useMachineStatusStore((s) => s.r2fDate);

  return (
    <ErrorBoundary identifier="R2FStatusDialog">
      <CommonDialog
        identifier={props.identifier}
        width={RADIX.DIALOG.WIDTH.MD}
        title="common.machine-status"
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <TableContent />

            {r2fDate && (
              <Text>
                {format(r2fDate, LOCAL_DATETIME_FORMAT_LONG, {
                  timeZone: LOCAL_TIMEZONE,
                })}
              </Text>
            )}
          </Flex>
        }
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
